import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
} from './productKnowledgeApiSlice';

import { PAGE_SIZE } from '../../constants';
import CustomDropdown from '../../components/CustomDropdown';
import { Modal, Pill, fieldStyle, labelStyle } from '../../components/adminUi';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { BandButton, ChipRow, Dash, ListPageShell, ListPagination, ListTable, SearchField, SquareButton, Tag, pillButton } from '../../components/listPage';

// Drawn with adminUi's Modal, like 파트너 관리's — not Semantic's stock form.
const ProductModal = ({ open, onClose, product = null }) => {
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category || '',
    support_url: product?.support_url || '',
    parsed_specs: product?.parsed_specs || '',
  });

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (product) {
        await updateProduct({ id: product.id, ...formData }).unwrap();
      } else {
        await createProduct(formData).unwrap();
      }
      onClose();
    } catch (err) {
      console.error('Failed to save product:', err);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, multiline) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={labelStyle}>{label}</label>
      {multiline ? (
        <textarea style={{ ...fieldStyle, minHeight: '120px', resize: 'vertical' }} value={formData[key]} onChange={e => setFormData(f => ({ ...f, [key]: e.target.value }))} />
      ) : (
        <input style={fieldStyle} value={formData[key]} onChange={e => setFormData(f => ({ ...f, [key]: e.target.value }))} />
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={product ? '제품 수정' : '제품 추가'}
      width={560}
      footer={
        <>
          <Pill variant="ghost" onClick={onClose} disabled={saving}>취소</Pill>
          <Pill variant="primary" onClick={handleSubmit} disabled={saving || !formData.name.trim() || !formData.category.trim()}>
            {saving ? '저장 중…' : '저장'}
          </Pill>
        </>
      }
    >
      {field('name', '제품명')}
      {field('category', '카테고리')}
      {field('support_url', 'Tech Specs URL')}
      {field('parsed_specs', '사양', true)}
    </Modal>
  );
};

const ProductManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const queryParams = new URLSearchParams(location.search);
  const page = Number(queryParams.get('page') || 1);
  const page_size = Number(queryParams.get('page_size') || PAGE_SIZE);
  const searchName = queryParams.get('name') || '';
  const searchCategory = queryParams.get('category') || '';
  const ordering = queryParams.get('ordering') || '-release_year';
  const [localSearchName, setLocalSearchName] = useState(searchName);
  const [filtersOpen, setFiltersOpen] = useState(!!searchCategory);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: allProducts } = useGetProductsQuery({ page: 1, page_size: 1000 });
  const categoryOptions = React.useMemo(() => {
    if (!allProducts?.results) return [];
    const unique = [...new Set(allProducts.results.map(p => p.category).filter(Boolean))];
    return unique.sort().map(cat => ({ key: cat, text: cat, value: cat }));
  }, [allProducts]);

  const { data: products, isLoading } = useGetProductsQuery({ page, page_size, name: searchName, category: searchCategory, ordering });

  const setQuery = updates => {
    const p = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!updates.page) p.delete('page');
    navigate(`${location.pathname}?${p.toString()}`);
  };
  const sortBy = f => setQuery({ ordering: ordering === `-${f}` ? f : `-${f}` });
  const search = () => setQuery({ name: localSearchName.trim() });

  const categoryDropdown = (
    <CustomDropdown
      searchable
      placeholder="카테고리 선택"
      options={categoryOptions}
      value={searchCategory}
      onChange={v => setQuery({ category: v })}
      icon="tag"
      style={isMobile ? { width: '100%' } : { minWidth: '180px', flex: '0 0 auto' }}
    />
  );
  const searchField = <SearchField isMobile={isMobile} value={localSearchName} onChange={setLocalSearchName} onSubmit={search} placeholder="제품명 검색" />;
  const sessionsLink = p => (
    <Link to={`/chatsessionslist?product=${p.id}`} onClick={e => e.stopPropagation()} style={{ color: '#007AFF', fontWeight: 600 }}>
      {p.session_count}
    </Link>
  );

  return (
    <ListPageShell
      isMobile={isMobile}
      icon="box"
      title="제품 정보 관리"
      subtitle="제품 지식 평가와 퀴즈의 기준이 되는 제품 사양"
      actions={<BandButton primary compact={isMobile} icon="plus" onClick={() => setIsModalOpen(true)}>제품 추가</BandButton>}
    >
      {isMobile ? (
        <>
          <div style={{ display: 'flex', gap: '8px' }}>
            {searchField}
            <SquareButton icon="sliders horizontal" label="필터" on={filtersOpen} set={!!searchCategory} onClick={() => setFiltersOpen(o => !o)} />
          </div>
          {filtersOpen && <div style={{ marginTop: '8px' }}>{categoryDropdown}</div>}
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {categoryDropdown}
          {searchField}
          <button type="button" onClick={search} style={pillButton(true)}>검색</button>
        </div>
      )}
      <ChipRow groups={[]} count={products?.count} />
      <ListTable
        isMobile={isMobile}
        loading={isLoading}
        rows={products?.results || []}
        ordering={ordering}
        onSort={sortBy}
        empty="제품이 없습니다."
        onRowClick={p => navigate(`/admin/products/${p.id}`)}
        columns={[
          { key: 'name', label: '제품명', sort: 'name', render: p => <span style={{ fontWeight: 600 }}>{p.name}</span> },
          { key: 'category', label: '카테고리', sort: 'category', render: p => (p.category ? <Tag tone="gray">{p.category}</Tag> : <Dash />) },
          { key: 'year', label: '출시 연도', sort: 'release_year', render: p => p.release_year || <Dash /> },
          { key: 'sessions', label: '세션 수', sort: 'session_count', render: sessionsLink },
        ]}
        card={{
          title: p => p.name,
          tags: p => [p.category && <Tag key="c" tone="gray">{p.category}</Tag>],
          meta: p => [p.release_year && `${p.release_year}년 출시`, `세션 ${p.session_count}개`],
        }}
      />
      <ListPagination page={page} count={products?.count || 0} pageSize={page_size} onChange={pg => setQuery({ page: String(pg) })} />
      {isModalOpen && <ProductModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />}
    </ListPageShell>
  );
};

export default ProductManagement;
